// Form validation helpers for the Booking & Scheduling module.

import { MeetingType } from "../types/booking";

export interface ValidationErrors {
  [field: string]: string;
}

export interface ServiceFormValues {
  name: string;
  description: string;
  durationMinutes: number | string;
  price: number | string | null;

  meetingType: MeetingType;
  meetingLocation: string;

  active: boolean;
  sortOrder: number | string;
}

export const validateServiceForm = (values: ServiceFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!values.name || !values.name.trim()) {
    errors.name = 'Service name is required.';
  } else if (values.name.trim().length > 120) {
    errors.name = 'Service name must be 120 characters or fewer.';
  }

  if (values.description && values.description.length > 1000) {
    errors.description = 'Description must be 1000 characters or fewer.';
  }

  const duration = Number(values.durationMinutes);
  if (!values.durationMinutes || Number.isNaN(duration) || duration <= 0) {
    errors.durationMinutes = 'Duration must be a positive number of minutes.';
  }

  // const price = Number(values.price);
  if (
    values.price !== '' &&
    values.price !== null &&
    values.price !== undefined
  ) {
    const price = Number(values.price);

    if (Number.isNaN(price) || price < 0) {
      errors.price = 'Price must be a valid, non-negative amount.';
    }
  }

  const sortOrder = Number(values.sortOrder);
  if (values.sortOrder !== '' && (Number.isNaN(sortOrder) || sortOrder < 0)) {
    errors.sortOrder = 'Sort order must be a non-negative number.';
  }

  if (
    values.meetingType === MeetingType.OnSite &&
    !values.meetingLocation.trim()
  ) {
    errors.meetingLocation =
      'Meeting location is required for on-site services.';
  }

  return errors;
};

export interface AvailabilityFormValues {
  dayOfWeek: number | '';
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export const validateAvailabilityForm = (values: AvailabilityFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (values.dayOfWeek === '') {
    errors.dayOfWeek = 'Please select a working day.';
  }

  if (!values.startTime) {
    errors.startTime = 'Start time is required.';
  }

  if (!values.endTime) {
    errors.endTime = 'End time is required.';
  } else if (values.startTime && values.endTime <= values.startTime) {
    errors.endTime = 'End time must be after start time.';
  }

  return errors;
};

export interface BookingFormValues {
  bookingServiceId: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  customerNotes: string;
  bookingDate: string; // yyyy-MM-dd
  bookingTime: string; // HH:mm
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+()\-\s0-9]{7,20}$/;

export const validateBookingForm = (values: BookingFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!values.bookingServiceId) errors.bookingServiceId = 'Please select a service.';
  if (!values.customerFirstName || !values.customerFirstName.trim()) errors.customerFirstName = 'First name is required.';
  if (!values.customerLastName || !values.customerLastName.trim()) errors.customerLastName = 'Last name is required.';

  if (!values.customerEmail) {
    errors.customerEmail = 'Email is required.';
  } else if (!EMAIL_REGEX.test(values.customerEmail)) {
    errors.customerEmail = 'Please enter a valid email address.';
  }

  if (values.customerPhone && !PHONE_REGEX.test(values.customerPhone)) {
    errors.customerPhone = 'Please enter a valid phone number.';
  }

  if (!values.bookingDate) errors.bookingDate = 'Please select a date.';
  if (!values.bookingTime) errors.bookingTime = 'Please select a time slot.';

  return errors;
};

export interface BookingSettingsFormValues {
  timezone: string;
  minimumNoticeHours: number | string;
  advanceBookingDays: number | string;
  defaultAppointmentDuration: number | string;
  bufferMinutes: number | string;
  bookingSlug: string;
  primaryColor: string;
  secondaryColor: string;
  buttonColor: string;
  accentColor: string;
}

export const validateBookingSettingsForm = (values: BookingSettingsFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!values.timezone) errors.timezone = 'Please select a timezone.';

  const notice = Number(values.minimumNoticeHours);
  if (values.minimumNoticeHours === '' || Number.isNaN(notice) || notice < 0) {
    errors.minimumNoticeHours = 'Minimum notice must be a non-negative number of hours.';
  }

  const window = Number(values.advanceBookingDays);
  if (!values.advanceBookingDays || Number.isNaN(window) || window <= 0) {
    errors.advanceBookingDays = 'Advance booking window must be a positive number of days.';
  }

  const duration = Number(values.defaultAppointmentDuration);
  if (!values.defaultAppointmentDuration || Number.isNaN(duration) || duration <= 0) {
    errors.defaultAppointmentDuration = 'Default duration must be a positive number of minutes.';
  }

  const buffer = Number(values.bufferMinutes);
  if (values.bufferMinutes !== '' && (Number.isNaN(buffer) || buffer < 0)) {
    errors.bufferMinutes = 'Buffer time must be a non-negative number.';
  }

  return errors;
};

export const hasValidationErrors = (errors: ValidationErrors): boolean => Object.keys(errors).length > 0;

